/*
 * WHAT A CANVAS ASKS THE CONSOLE'S API FOR: the tenant (its location and time zone, when the
 * caller did not pass them), the weather at a location and the tenant's Imprion POS menu. The
 * player never gets here — it passes all of them itself — so this uses the console's signed-in
 * fetch store, and reaches the canvases only as the data source `main.ts` provides
 * (`canvasData.ts`), never by an import from the renderer.
 *
 * All are cached per page: many canvases (a gallery, the editor and its preview) share one
 * request, and weather is refreshed at most every 15 minutes (the API caches it as long).
 */
import { reactive } from 'vue'

import type { CanvasDataSource } from '@/design/libs/canvasData'
import type { Location, Menu, TenantInfo, Weather } from '@/design/types'
import { useFetchStore } from '@/stores/fetch'

const WEATHER_TTL = 15 * 60 * 1000

const tenants = reactive(new Map<string, TenantInfo | null>())
const tenantRequests = new Map<string, Promise<void>>()

/** Asks for the tenant once per page (a side effect — call it from a watcher, not a render). */
export const loadTenantInfo = (tenantId: string) => {
    if (!tenantId || tenantRequests.has(tenantId)) return

    const fetchStore = useFetchStore()

    tenantRequests.set(tenantId, (async () => {
        const { statusCode, data } = await fetchStore.fetch(`/tenants/${tenantId}`).get().json<TenantInfo>()

        tenants.set(tenantId, statusCode.value === 200 && data.value ? data.value : null)
    })())
}

/** The tenant, once `loadTenantInfo` has brought it; null before that. */
export const tenantInfo = (tenantId: string): TenantInfo | null => tenants.get(tenantId) ?? null

/** Remember a tenant the caller has already loaded (the editor), so no second request goes out. */
export const primeTenantInfo = (tenant: TenantInfo) => {
    tenants.set(tenant.id, tenant)
    tenantRequests.set(tenant.id, Promise.resolve())
}

interface WeatherEntry {
    weather: Weather | null
    at:      number
    pending: boolean
}

const weathers = reactive(new Map<string, WeatherEntry>())

export const locationKey = (loc: Location): string => `${loc.lat.toFixed(2)},${loc.lon.toFixed(2)}`

const weatherKey = (tenantId: string, loc: Location) => `${tenantId}|${locationKey(loc)}`

/**
 * Asks for the weather at a location unless a fresh answer (under 15 minutes) or a request is
 * already there. A side effect — call it from a watcher or a timer, not from a render.
 */
export const requestWeather = (tenantId: string, loc: Location) => {
    const key   = weatherKey(tenantId, loc)
    const entry = weathers.get(key)

    if (entry && (entry.pending || Date.now() - entry.at < WEATHER_TTL)) return

    const fetchStore = useFetchStore()

    weathers.set(key, { weather: entry?.weather ?? null, at: Date.now(), pending: true })

    void (async () => {
        const { statusCode, data } = await fetchStore
            .fetch(`/tenants/${tenantId}/weather?lat=${loc.lat}&lon=${loc.lon}`)
            .get()
            .json<Weather>()
        const current = weathers.get(key)

        if (!current) return
        if (statusCode.value === 200 && data.value) current.weather = data.value
        current.pending = false
        current.at      = Date.now()
    })()
}

/** The last known weather at a location; null before the first answer or when there is none. */
export const weatherAt = (tenantId: string, loc: Location): Weather | null =>
    weathers.get(weatherKey(tenantId, loc))?.weather ?? null

// --- the Imprion POS menu (menu and happy-hour elements; the player passes `manifest.menu`) ---

/** `GET /tenants/{tid}/menu` — the console's answer carries the sync time too. */
export interface MenuAnswer extends Menu {
    synced_at: string | null
}

/** tenant id → its menu once asked (null = none: no POS yet, or the request failed). */
const menus = reactive(new Map<string, MenuAnswer | null>())
const menuRequests = new Map<string, Promise<void>>()

const fetchMenu = (tenantId: string): Promise<void> => {
    const fetchStore = useFetchStore()
    const request    = (async () => {
        const { statusCode, data } = await fetchStore.fetch(`/tenants/${tenantId}/menu`).get().json<MenuAnswer>()

        menus.set(tenantId, statusCode.value === 200 && data.value ? data.value : null)
    })()

    menuRequests.set(tenantId, request)

    return request
}

/** Asks for the tenant's menu once per page (a side effect — call it from a watcher, not a render). */
export const loadMenu = (tenantId: string) => {
    if (!tenantId || menuRequests.has(tenantId)) return
    void fetchMenu(tenantId)
}

/**
 * Asks again — for a console page that has just synced the POS or changed a product's display,
 * so the editor and every thumbnail on the page show the new menu. Only when it was asked for.
 */
export const reloadMenu = (tenantId: string): Promise<void> =>
    menuRequests.has(tenantId) ? fetchMenu(tenantId) : Promise.resolve()

/** Remember a menu the caller has already loaded, so no second request goes out. */
export const primeMenu = (tenantId: string, menu: MenuAnswer | null) => {
    menus.set(tenantId, menu)
    menuRequests.set(tenantId, Promise.resolve())
}

/** The tenant's menu: undefined while it is being asked for, null when there is none. */
export const menuOf = (tenantId: string): MenuAnswer | null | undefined =>
    menus.has(tenantId) ? menus.get(tenantId) ?? null : undefined

/** All of the above as the canvases' data source (`canvasData.ts`), provided by `main.ts`. */
export const consoleCanvasData: CanvasDataSource = { loadTenantInfo, tenantInfo, requestWeather, weatherAt, loadMenu, menuOf }
